import { LiquidProperties } from "@/Apps/Funnels/SimpleBuilder/LiquidProperties";
import { Page } from "@/ui/Layout";
import {
  Browser,
  CaretDoubleLeft,
  CaretDoubleRight,
  Eye,
} from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { MobileState } from "../MobileResponsiveToggle/types";
import ListDropdown from "@/ui/ListDropdown";
import { DownCarrotIcon } from "@/ui/Icons";
import Button from "@/ui/Button";
import { ButtonTypes, Variants } from "@/ui/types";
import { PageContext } from "../Page/PageContext";
import { loadFonts } from "@/Apps/Pages/Page/Preview";
import { Loading } from "@/ui/Layout/Loading";
import WebFont from "webfontloader";
import { mapColorsToVariables, remapCampaignVariables } from "../utils";
import { uniq } from "lodash";
import PureCampaignBuilder from "@/Apps/Funnels/CampaignBuilder/PureCampaignBuilder";
import { PagePreviewNode } from "@/Apps/AITools/LangGraphBot/CampaignAgent.stories";
import { getObjects } from "@/api/campaigns";
import { Handle, Position } from "reactflow";
import { PageContent } from "../Page/Page";
import { TopbarButton } from "@/ui/Layout/Topbar/Topbar";
import { Grid3X3Icon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SwitchGroup } from "../Properties/components/groups/SwitchGroup/SwitchGroup";
import AceEditor from "react-ace";
import { updatePage } from "@/api/pages";
import { getSessionToken } from "@/api/auth";

// Import Ace editor modes and themes to avoid dynamic loading issues
import "ace-builds/src-noconflict/mode-javascript";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/ext-searchbox";
import "ace-builds/src-noconflict/ext-language_tools";
import { LiquidStyles } from "../Page/LiquidRenderer";

const CustomNode = (props: any) => {
  const { data, sourcePosition, targetPosition, width, height } = props;
  const { label, type, content = [] } = data;
  const originalWidth = 1366;
  const originalHeight = 768;
  return (
    <div style={{ width, height }} className="w-full h-full overflow-hidden">
      <div className="text-sm">{label}</div>
      <div
        style={{
          transform: `scale(${width / originalWidth})`,
          transformOrigin: "top left",
          width: originalWidth,
          height: originalHeight,
        }}
        className="overflow-auto rounded-lg hover:opacity-50 cursor-pointer"
        title="Double click to edit"
      >
        {content?.length > 1 ? (
          <PageContent
            key={JSON.stringify(content)}
            id={String(data?.id)}
            content={JSON.stringify(content)}
            nextPage="#"
            getPageData={() => content}
            trackingPayload=""
            seoPayload=""
            merchantPayload=""
            emailPayload=""
            cssPayload=""
            exitPayload=""
            objectId={data?.id}
            campaignId={data?.campaignId}
            userId={data?.userId}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center bg-black/10 dark:bg-white/10 rounded-lg"
            style={{ height: originalHeight }}
          >
            <div className="text-3xl text-gray-500 font-bold">
              No content found
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const SplitEditor = ({
  campaignData,
  setCampaignData = (data: { settings: any }) => {},
  content = [],
  pageId,
  objectId,
  mobileState,
  setMobileState,
  scale = 50,
}: {
  campaignData: any;
  setCampaignData: (data: any) => void;
  content: any;
  pageId: string;
  mobileState: { type: MobileState };
  setMobileState: (data: { type: MobileState }) => void;
  scale?: number;
}) => {
  const [isPropertiesSidebarShowing, setIsPropertiesSidebarShowing] =
    useState(false);
  const [scaleValue, setScaleValue] = useState<number>(scale);
  const [campaignObjects, setCampaignObjects] = useState<any[]>([]);
  const campaignBuilderRef = useRef<any>(null);
  const [rows, setRows] = useState<number>(3);
  const [canvasType, setCanvasType] = useState<"grid" | "page">("page");
  const [sourceIsShowing, setSourceIsShowing] = useState(false);
  const [textContent, setTextContent] = useState(
    JSON.stringify(content, null, 2)
  );
  const [revisedObjects, setRevisedObjects] = useState<any[]>(content);

  const navigate = useNavigate();

  const width = 390;
  const height = 270;
  const spacing = 20;

  const topBarSettings = {
    hideCopilotButton: true,
    page: [
      { label: "Live Preview", icon: <Eye className="text-lg" />, href: "#" },
      { label: campaignData?.name || "•••" },
    ],
    items: (
      <>
        <TopbarButton
          onClick={() => setCanvasType(canvasType === "grid" ? "page" : "grid")}
        >
          {canvasType === "grid" ? (
            <Grid3X3Icon className="text-lg" />
          ) : (
            <Browser className="text-lg" />
          )}
        </TopbarButton>
        {canvasType === "grid" && (
          <ListDropdown
            element={
              <Button
                label={
                  <div className="flex gap-1">
                    <span className="">Rows: {rows}</span>
                    <DownCarrotIcon className="pt-1" />
                  </div>
                }
                type={ButtonTypes.OUTLINED}
                variant={Variants.INFO}
              />
            }
            items={[
              {
                id: "1",
                label: "1 per row",
                onClick: () => setRows(1),
              },
              {
                id: "2",
                label: "2 per row",
                onClick: () => setRows(2),
              },
              {
                id: "3",
                label: "3 per row",
                onClick: () => setRows(3),
              },
              {
                id: "4",
                label: "4 per row",
                onClick: () => setRows(4),
              },
            ]}
          />
        )}
        {canvasType === "page" && (
          <>
            <ListDropdown
              element={
                <Button
                  label={
                    <div className="flex gap-1">
                      <span className="">{scaleValue}%</span>
                      <DownCarrotIcon className="pt-1" />
                    </div>
                  }
                  type={ButtonTypes.OUTLINED}
                  variant={Variants.INFO}
                />
              }
              items={[
                {
                  id: "25",
                  label: "25%",
                  onClick: () => setScaleValue(25),
                },
                {
                  id: "50",
                  label: "50%",
                  onClick: () => setScaleValue(50),
                },
                {
                  id: "75",
                  label: "75%",
                  onClick: () => setScaleValue(75),
                },
                {
                  id: "100",
                  label: "100%",
                  onClick: () => setScaleValue(100),
                },
              ]}
            />
            <Button
              label="Remap Colors"
              type={ButtonTypes.DEFAULT}
              variant={Variants.INFO}
              onClick={() => {
                setRevisedObjects(mapColorsToVariables(revisedObjects));
              }}
            />
            {!sourceIsShowing && revisedObjects !== content && (
              <Button
                label="Save Changes"
                type={ButtonTypes.DEFAULT}
                variant={Variants.SUCCESS}
                onClick={async () => {
                  // Save the current revisedObjects to textContent (or trigger save logic)
                  // setTextContent(JSON.stringify(revisedObjects, null, 2));
                  // You can add additional save logic here if needed
                  const response = await updatePage(pageId, {
                    token: getSessionToken(),
                    settings: { content: revisedObjects },
                  });
                  console.log("Save Page Response", response);
                  window.location.reload();
                }}
                className="ml-2"
              />
            )}
            <SwitchGroup
              label={
                <p className="text-lg text-black dark:text-white">
                  Show Source
                </p>
              }
              className="p-0"
              isChecked={sourceIsShowing}
              onClick={() => {
                if (sourceIsShowing) {
                  setRevisedObjects(JSON.parse(textContent));
                } else {
                  setTextContent(JSON.stringify(revisedObjects, null, 2));
                }
                setSourceIsShowing(!sourceIsShowing);
              }}
            />
          </>
        )}
      </>
    ),
  };

  const fetchCampaignObjects = async () => {
    const { data } = await getObjects(campaignData?.id);
    console.log("campaign objects", data);
    setCampaignObjects(
      data?.objects.map((object: any, index: number) => ({
        ...object,
        // Arrange in a grid: 3 objects per row
        x: (index % rows) * (width + spacing),
        y: Math.floor(index / rows) * (height + spacing),
        width,
        height,
        connectTo: [],
        content: object?.id === objectId ? content : object?.page?.content,
      }))
    );
    // campaignBuilderRef.current?.fitView();
    campaignBuilderRef.current?.fitViewWidth();
  };

  useEffect(() => {
    setCampaignObjects(
      campaignObjects.map((object: any, index: number) => ({
        ...object,
        x: (index % rows) * (width + spacing),
        y: Math.floor(index / rows) * (height + spacing),
      }))
    );
    setTimeout(() => {
      campaignBuilderRef.current?.fitViewWidth();
    }, 200);
  }, [rows, setRows]);

  useEffect(() => {
    // if (!content || !campaignData || !objectId || !content.length) return;
    if (!campaignData || !content?.length) return;

    (async () => {
      await fetchCampaignObjects();
      loadFonts(content);
      setRevisedObjects(content);
      const variables = campaignData?.settings?.variables || [];
      const { typeface } = remapCampaignVariables(variables);
      if (typeface) {
        const typefaceList = Object.values(typeface);
        if (typefaceList.length)
          WebFont.load({
            google: { families: uniq(typefaceList) as string[] },
          });
      }
    })();
  }, [content, campaignData]);

  // if (!content || !campaignData || !objectId || !content.length || !campaignObjects.length) return <Loading type="gallery" />;
  if (!campaignObjects.length || !content?.length)
    return <Loading type="gallery" />;

  return (
    <div className="flex-1 border border-black/10 dark:border-white/10 rounded-lg m-4">
      <Page topBar={topBarSettings}>
        <PageContext.Provider
          value={{
            activeContent: content,
            setContent: () => {}, // state obj
            activeSettings: {}, // state obj
            campaignSettings: campaignData?.settings,
            parseLiquid: true,
            updateCompletedItems: () => {},
            completedItems: campaignData?.settings?.completedItems || [],
            mobileState: mobileState.type,
            setMobileState: (state) => setMobileState({ type: state }),
          }}
        >
          <LiquidStyles />
          {/* Properties */}
          <button
            onClick={() =>
              setIsPropertiesSidebarShowing(!isPropertiesSidebarShowing)
            }
            className="absolute z-10 border-r border-b border-black/10 dark:border-white/10 bg-white/90 dark:bg-black/90 backdrop-blur dark:text-black dark:text-white text-[18px] cursor-pointer w-7 h-7 rounded-br flex items-center justify-center transition-colors duration-200 dark:hover:bg-white/10 hover:bg-black/10"
            aria-label="Close properties sidebar"
            title="Close"
            tabIndex={0}
            style={{ marginLeft: isPropertiesSidebarShowing ? 400 : 0 }}
          >
            {isPropertiesSidebarShowing ? (
              <CaretDoubleLeft size={12} weight="bold" />
            ) : (
              <CaretDoubleRight size={12} weight="bold" />
            )}
          </button>
          {isPropertiesSidebarShowing && (
            <div className="w-[400px] absolute bg-white/90 dark:bg-black/90 backdrop-blur z-10 divide-y divide-black/10 dark:divide-white/10 border-r border-black/10 dark:border-white/10 h-full overflow-y-auto">
              <LiquidProperties
                campaignData={campaignData}
                // setCampaignData={(data) => alert(JSON.stringify(data))}
                setCampaignData={setCampaignData}
                setIsLoading={() => {}}
                setContent={() => {}}
                isExpanded={true}
              />
            </div>
          )}
          {canvasType === "page" && !sourceIsShowing && (
            <div
              className="w-full bg-white"
              style={{
                transform: `scale(${scaleValue / 100}) ${scaleValue < 100 ? "translateY(100px)" : ""}`,
                transformOrigin: "top center",
              }}
            >
              {revisedObjects.length > 0 && (
                <PageContent
                  key={JSON.stringify(revisedObjects)}
                  id={String(pageId)}
                  content={JSON.stringify(revisedObjects)}
                  // content={JSON.stringify([])}
                  nextPage="#"
                  getPageData={() => revisedObjects}
                  trackingPayload=""
                  seoPayload=""
                  merchantPayload=""
                  emailPayload=""
                  cssPayload=""
                  exitPayload=""
                  objectId={objectId}
                  campaignId={campaignData?.id}
                  userId={campaignData?.userId}
                />
              )}
            </div>
          )}
          {sourceIsShowing && (
            <div className="w-full h-full bg-black/10">
              <AceEditor
                mode="javascript"
                theme="github"
                onChange={setTextContent}
                value={textContent}
                name="UNIQUE_ID_OF_DIV"
                editorProps={{ $blockScrolling: true }}
                width="100%"
                height="100%"
                className="w-full h-full"
                enableBasicAutocompletion={true}
                enableLiveAutocompletion={true}
                showPrintMargin={false}
                showGutter={true}
                highlightActiveLine={true}
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  showLineNumbers: true,
                  tabSize: 2,
                }}
              />
            </div>
          )}
          {canvasType === "grid" && !sourceIsShowing && (
            <PureCampaignBuilder
              // key={rows}
              id="campaign-builder"
              ref={campaignBuilderRef}
              objects={campaignObjects}
              onChange={() => {}}
              deleteConnection={() => {}}
              updateObject={() => {}}
              deleteObject={() => {}}
              createConnection={() => {}}
              createObject={() => {}}
              getNodeDropdownItems={() => {}}
              // showControls={false}
              // showMiniMap={false}
              panOnScroll={true}
              readOnly={true}
              CustomNode={(props) => (
                <CustomNode {...props} width={width} height={height} />
              )}
              onNodeDoubleClick={(event, node) => {
                // console.log("onNodeDoubleClick", event, node);
                const { id } = node?.data || {};
                window.location.href = `/funnels/${campaignData?.id}/${id}`;
              }}
            />
          )}
        </PageContext.Provider>
      </Page>
    </div>
  );
};
